import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as moment from "moment";
import {
  TABLE_LEADS_LIST,
  LEAD_LIST_ENDPOINT,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { loadAllLead } from "./LeadList.actionCreators";
import { setAllLeadLoadingState } from "./LeadList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { columnHeader, isEmpty } from "@utils/utils";
import LeadInfo from "./components/LeadInfo/LeadInfo";
import LeadStatus from "./components/LeadStatus/LeadStatus";
import LeadAction from "./components/LeadAction/LeadAction";
import { leadStatus } from "@utils/filterOptions";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    countryList,
    selectedFilter,
    additionalData,
    techAdmin,
    userCountryId,
  } = props;

  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState(additionalData);
  const dataURL = LEAD_LIST_ENDPOINT;
  const dispatch = useDispatch();

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "idInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => <LeadInfo data={value.id} column="id" />,
      disableFilters: false,
    },
    {
      Header: t("LEAD_DATE"),
      title: "LEAD_DATE",
      accessor: "date",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <LeadInfo data={value.date} column="date" />
      ),
      disableFilters: true,
    },
    {
      Header: t("LEAD_CONTACT"),
      title: "LEAD_CONTACT",
      accessor: "contactInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => <LeadInfo data={value} column="contact" />,
      disableFilters: true,
    },
    {
      Header: t("LEAD_SITE"),
      title: "LEAD_SITE",
      accessor: "siteInfo",
      Cell: ({ cell: { value } }) => <LeadInfo data={value} column="site" />,
      disableFilters: true,
    },
    {
      Header: t("LEAD_STATUS"),
      title: "LEAD_STATUS",
      accessor: "statusInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <LeadStatus data={value} column="contact" />
      ),
      disableFilters: true,
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <LeadAction data={value} updateRefeshStatus={updateRefeshStatus} />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const leadListByIds = useSelector((state) => state.leadList.byId);
  const leadListAllIds = useSelector((state) => state.leadList.allIds);
  const filteredRecords = useSelector(
    (state) => state.leadList.filteredRecords
  );
  const isLeadListLoading = useSelector((state) => state.leadList.isLoading);

  useEffect(() => {
    setLoading(isLeadListLoading);
  }, [isLeadListLoading]);
  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });

    let statusFilters = [];
    let countryFilters = [];

    if (additionalFilters.length > 0) {
      const countryFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "country"
      );
      countryFilters = countryFilter?.map((item) => item.value);

      const statusFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "status"
      );
      statusFilters = statusFilter?.map((item) => item.value);
    }
    if (!techAdmin) {
      countryFilters = userCountryId;
    }
    // Set the loading state
    setLoading(true);
    dispatch(setAllLeadLoadingState());
    dispatch(
      loadAllLead({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        statusFilters,
        countryFilters,
      })
    );
  };

  useEffect(() => {
    if (leadListAllIds.length > 0) {
      const responseData = leadListAllIds.map((id) => {
        const tempData = { ...leadListByIds[id] };
        tempData.idInfo = {
          id: tempData.id,
        };
        tempData.date = {
          date: tempData.createdAt,
        };
        tempData.contactInfo = {
          contact: tempData.contact,
          companyname: tempData.company?.id
            ? tempData.company.name
            : tempData.tempCompany?.name,
          companyId: tempData.company?.id,
        };
        tempData.siteInfo = {
          address: tempData.address,
          site: tempData.site,
          enquiry: tempData.leadEnquiries,
          totalEnquiries: tempData.totalEnquiries,
        };
        tempData.statusInfo = {
          status: tempData.status,
        };
        tempData.action = {
          id: tempData.id,
          status: tempData.status,
          step: tempData.step,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, leadListAllIds, leadListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: additionalFilters,
      });
    }
  }, [additionalFilters]);

  return (
    <EncloseTable
      type={TABLE_LEADS_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={30}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      setAdditionalFilters={setAdditionalFilters}
      additionalFilters={additionalFilters}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      selectedFilter={selectedFilter}
      extraFilters={[
        ...(countryList?.length > 1
          ? [
              {
                type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
                label: t("LEADS_FILTER_COUNTRY"),
                placeholder: t("SEARCH"),
                name: "country",
                labelClass: "leads-list__status-list-label",
                containerClass: "leads-list__status-list-dropdown-container",
                defaultOption: "Nothing Selected",
                options: countryList,
              },
            ]
          : []),
        {
          type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
          label: t("LEADS_FILTER_STATUS"),
          placeholder: t("SEARCH"),
          name: "status",
          labelClass: "leads-list__status-list-label",
          containerClass: "leads-list__status-list-dropdown-container",
          defaultOption: "Nothing Selected",
          options: leadStatus(t),
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  updateRefeshStatus: PropTypes.func,
  techAdmin: PropTypes.bool,
};
PrepareDataTable.defaultProps = {
  updateRefeshStatus: {},
  techAdmin: false,
};

export default withTranslation()(PrepareDataTable);
